import type { ExerciseDef } from './types'

// ─── 3.1 Vectors ──────────────────────────────────────────────────────────────

export const featureVectors: ExerciseDef = {
  starter: `// In machine learning, every example becomes a vector: a list of numbers.
// A house: [size in m², bedrooms, age in years]
const house = [120, 3, 15]

// A colour: [red, green, blue], each 0–255
const tomato = [255, 99, 71]

// A (tiny) word embedding — you'll meet these in Module 8:
const cat = [0.8, -0.2, 0.45, 0.1]

console.log(house.length, "numbers describe the house")
console.log(cat.length, "numbers describe 'cat'")
`,
}

export const vectorOps: ExerciseDef = {
  starter: `// 1. add(a, b): element by element → add([1, 2], [3, 4]) = [4, 6]
function add(a: number[], b: number[]): number[] {
  return []
}

// 2. scale(a, k): multiply every element by k → scale([1, 2], 3) = [3, 6]
function scale(a: number[], k: number): number[] {
  return []
}

// 3. norm(a): the length √(a₀² + a₁² + …) → norm([3, 4]) = 5
function norm(a: number[]): number {
  return 0
}

console.log(add([1, 2], [3, 4]), scale([1, 2], 3), norm([3, 4]))
`,
  solution: `function add(a: number[], b: number[]): number[] {
  return a.map((ai, i) => ai + b[i])
}

function scale(a: number[], k: number): number[] {
  return a.map(ai => ai * k)
}

function norm(a: number[]): number {
  let total = 0
  for (const ai of a) total += ai * ai
  return Math.sqrt(total)
}

console.log(add([1, 2], [3, 4]), scale([1, 2], 3), norm([3, 4]))
`,
  tests: `
test("add([1, 2], [3, 4]) is [4, 6]", () => expect(add([1, 2], [3, 4]), "add([1, 2], [3, 4])").toEqual([4, 6]))
test("add works in 3D", () => expect(add([1, 0, -1], [0.5, 2, 1]), "add([1, 0, -1], [0.5, 2, 1])").toEqual([1.5, 2, 0]))
test("scale([1, 2], 3) is [3, 6]", () => expect(scale([1, 2], 3), "scale([1, 2], 3)").toEqual([3, 6]))
test("scale by −1 flips the vector", () => expect(scale([2, -5], -1), "scale([2, -5], -1)").toEqual([-2, 5]))
test("norm([3, 4]) is 5", () => expect(norm([3, 4]), "norm([3, 4])").toBeCloseTo(5))
test("norm works in 3D: [2, 3, 6] → 7", () => expect(norm([2, 3, 6]), "norm([2, 3, 6])").toBeCloseTo(7))
`,
}

// ─── 3.2 Dot product ──────────────────────────────────────────────────────────

export const shoppingDot: ExerciseDef = {
  starter: `// The dot product as a shopping bill: quantities · prices
const quantities = [2, 1, 3]        // apples, bread, milk
const prices     = [0.5, 2.2, 1.1]

let bill = 0
for (let i = 0; i < quantities.length; i++) {
  bill += quantities[i] * prices[i]
}
console.log("total:", bill)
console.log("same thing with the built-in:", vec.dot(quantities, prices))
`,
}

export const dotCosine: ExerciseDef = {
  starter: `// 1. dot(a, b) = Σ aᵢ·bᵢ  → dot([1, 2, 3], [4, 5, 6]) = 32
function dot(a: number[], b: number[]): number {
  return 0
}

// 2. cosineSimilarity(a, b) = dot(a, b) / (|a| · |b|)
//    1 = same direction, 0 = unrelated (perpendicular), −1 = opposite.
//    You may use vec.norm(...) for the lengths.
function cosineSimilarity(a: number[], b: number[]): number {
  return 0
}

console.log(dot([1, 2, 3], [4, 5, 6]), cosineSimilarity([1, 0], [1, 1]))
`,
  solution: `function dot(a: number[], b: number[]): number {
  let total = 0
  for (let i = 0; i < a.length; i++) total += a[i] * b[i]
  return total
}

function cosineSimilarity(a: number[], b: number[]): number {
  return dot(a, b) / (vec.norm(a) * vec.norm(b))
}

console.log(dot([1, 2, 3], [4, 5, 6]), cosineSimilarity([1, 0], [1, 1]))
`,
  tests: `
test("dot([1, 2, 3], [4, 5, 6]) is 32", () => expect(dot([1, 2, 3], [4, 5, 6]), "dot([1, 2, 3], [4, 5, 6])").toBe(32))
test("perpendicular vectors have dot 0", () => expect(dot([2, 0], [0, 7]), "dot([2, 0], [0, 7])").toBe(0))
test("opposite vectors have negative dot", () => expect(dot([1, 1], [-2, -2]), "dot([1, 1], [-2, -2])").toBe(-4))
test("cosine of identical directions is 1", () => expect(cosineSimilarity([3, 4], [6, 8]), "cosineSimilarity([3, 4], [6, 8])").toBeCloseTo(1))
test("cosine of perpendicular vectors is 0", () => expect(cosineSimilarity([1, 0], [0, 5]), "cosineSimilarity([1, 0], [0, 5])").toBeCloseTo(0))
test("cosine at 45° is about 0.707", () => expect(cosineSimilarity([1, 0], [1, 1]), "cosineSimilarity([1, 0], [1, 1])").toBeCloseTo(Math.SQRT1_2))
`,
}

export const similarWords: ExerciseDef = {
  starter: `// Tiny made-up "meaning vectors": [is-animal, is-royal, is-food, is-cute]
const words: Record<string, number[]> = {
  cat:    [0.9, 0.0, 0.1, 0.9],
  kitten: [0.9, 0.0, 0.0, 1.0],
  king:   [0.1, 1.0, 0.0, 0.1],
  queen:  [0.1, 1.0, 0.0, 0.3],
  apple:  [0.0, 0.0, 1.0, 0.2],
}

// Which word is most similar to "cat"? Rank them all by cosine similarity.
const ranked = Object.keys(words)
  .filter(w => w !== "cat")
  .map(w => [w, vec.cosine(words.cat, words[w])] as const)
  .sort((a, b) => b[1] - a[1])

for (const [w, s] of ranked) console.log(w.padEnd(8), s.toFixed(3))
`,
}

// ─── 3.3 Matrices ─────────────────────────────────────────────────────────────

export const matrixShape: ExerciseDef = {
  starter: `// A dataset is a matrix: one row per example, one column per feature.
const houses = [
  // size  rooms  age
  [  60,    2,    30],
  [  95,    3,    12],
  [ 120,    3,     5],
  [ 150,    4,     2],
]
console.log("shape:", mat.shape(houses))     // [rows, columns]
console.log("the 3rd house:", houses[2])
console.log("every house's size:", mat.col(houses, 0))
`,
}

export const matVec: ExerciseDef = {
  starter: `// matVec(A, v): multiply a matrix by a vector.
// Each entry of the result is one ROW of A dotted with v.
//   [[1, 2],    [5,     [1·5 + 2·6,     [17,
//    [3, 4]] ×   6]  =   3·5 + 4·6]  =   39]
function matVec(A: number[][], v: number[]): number[] {
  return []
}

console.log(matVec([[1, 2], [3, 4]], [5, 6]))
`,
  solution: `function matVec(A: number[][], v: number[]): number[] {
  return A.map(row => {
    let total = 0
    for (let j = 0; j < v.length; j++) total += row[j] * v[j]
    return total
  })
}

console.log(matVec([[1, 2], [3, 4]], [5, 6]))
`,
  tests: `
test("[[1,2],[3,4]] × [5,6] is [17, 39]", () => expect(matVec([[1, 2], [3, 4]], [5, 6]), "matVec(...)").toEqual([17, 39]))
test("the identity matrix changes nothing", () => expect(matVec([[1, 0], [0, 1]], [7, -3]), "matVec(identity, [7, -3])").toEqual([7, -3]))
test("rotating [1, 0] by 90° gives [0, 1]", () => expect(matVec([[0, -1], [1, 0]], [1, 0]), "matVec(rotate90, [1, 0])").toEqual([0, 1]))
test("works for non-square matrices (2×3 × 3)", () => expect(matVec([[1, 0, 2], [0, 1, 1]], [1, 2, 3]), "matVec(2×3, [1,2,3])").toEqual([7, 5]))
`,
}

// ─── 3.4 Matrix multiplication ────────────────────────────────────────────────

export const matMul: ExerciseDef = {
  starter: `// 1. transpose(A): rows become columns. A 2×3 matrix becomes 3×2.
function transpose(A: number[][]): number[][] {
  return []
}

// 2. matMul(A, B): C[i][j] = (row i of A) · (column j of B)
//    A is n×k, B is k×m, C is n×m.
function matMul(A: number[][], B: number[][]): number[][] {
  return []
}

console.log(transpose([[1, 2, 3], [4, 5, 6]]))
console.log(matMul([[1, 2], [3, 4]], [[5, 6], [7, 8]]))
`,
  solution: `function transpose(A: number[][]): number[][] {
  return A[0].map((_, j) => A.map(row => row[j]))
}

function matMul(A: number[][], B: number[][]): number[][] {
  const n = A.length
  const k = B.length
  const m = B[0].length
  const C: number[][] = []
  for (let i = 0; i < n; i++) {
    C.push([])
    for (let j = 0; j < m; j++) {
      let total = 0
      for (let p = 0; p < k; p++) total += A[i][p] * B[p][j]
      C[i].push(total)
    }
  }
  return C
}

console.log(transpose([[1, 2, 3], [4, 5, 6]]))
console.log(matMul([[1, 2], [3, 4]], [[5, 6], [7, 8]]))
`,
  tests: `
test("transpose of a 2×3 is 3×2", () => expect(transpose([[1, 2, 3], [4, 5, 6]]), "transpose(...)").toEqual([[1, 4], [2, 5], [3, 6]]))
test("[[1,2],[3,4]] × [[5,6],[7,8]]", () => expect(matMul([[1, 2], [3, 4]], [[5, 6], [7, 8]]), "matMul(...)").toEqual([[19, 22], [43, 50]]))
test("(2×3) × (3×2) gives a 2×2", () => expect(matMul([[1, 2, 0], [3, -1, 2]], [[2, 1], [0, 3], [1, -2]]), "matMul(...)").toEqual([[2, 7], [8, -4]]))
test("multiplying by the identity changes nothing", () => expect(matMul([[2, 3], [4, 5]], [[1, 0], [0, 1]]), "matMul(A, I)").toEqual([[2, 3], [4, 5]]))
test("a row times a column is a 1×1 dot product", () => expect(matMul([[1, 2, 3]], [[4], [5], [6]]), "matMul(row, column)").toEqual([[32]]))
`,
}

export const orderMatters: ExerciseDef = {
  starter: `const rotate = [[0, -1], [1, 0]]   // rotate 90° counter-clockwise
const shear  = [[1, 1], [0, 1]]    // slide the top to the right

// Matrix order is right-to-left: (A·B)·v means "apply B first, then A".
console.log("rotate·shear =", mat.mul(rotate, shear))
console.log("shear·rotate =", mat.mul(shear, rotate))
console.log("same?", JSON.stringify(mat.mul(rotate, shear)) === JSON.stringify(mat.mul(shear, rotate)))
`,
}

export const batchLayer: ExerciseDef = {
  starter: `// A layer with 3 inputs and 2 outputs is a 3×2 weight matrix W.
// A batch of 4 examples is a 4×3 matrix X (one row each).
// One matrix multiply X·W computes the layer for ALL examples at once.
random.seed(1)
const X = mat.random(4, 3)
const W = mat.random(3, 2)

const Y = mat.mul(X, W)
console.log("X is", mat.shape(X), " W is", mat.shape(W), " → Y is", mat.shape(Y))
console.log(Y)
`,
}
